import React, { Fragment, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { catalogApi, shopApi } from '@app/store';
import { ICatalog, CatalogDTO } from '@app/common';
import { Button, CardButtonActionMode, Form, Panel } from '@app/components';
import { CatalogForm } from './CatalogForm';
import { CatalogFormValues, transformToUpdatableFormCatalog, useCatalogFormSubmit } from './catalog-form.helpers';
import { useToast, useTriptych } from '@app/hooks';
import { extractData } from '@app/helpers/utils';
import { InputFileRef } from '@app/components/form/InputFile';
import { CatalogCard } from './CatalogCard';
import { extractApiGenericError } from '@app/helpers';
import { CheckPermissions } from '@app/components/CheckPermissions';
import { valeursDeCopieDuCatalogue } from '../Duplication/valeurs-de-copie';

interface CatalogUpdateProps {
  catalog?: Partial<ICatalog>;
  isFetching?: boolean;
  toggle: (mode: CardButtonActionMode) => void;
  onDelete: (product: Partial<ICatalog>) => void;
}

export const CatalogUpdate = ({ catalog, isFetching, toggle, onDelete }: CatalogUpdateProps) => {
  const toast = useToast();

  const triptych = useTriptych();

  const cardImageRef = useRef<InputFileRef>();

  const shopIds = useMemo(() => catalog?.catalogToShops?.map(catalogToShop => catalogToShop.shopId), [catalog]);

  const { data: paginatedShops } = shopApi.useReadManyShopsQuery({
    filters: {
      _id: {
        $in: shopIds
      }
    },
  }, { skip: !shopIds?.length });

  const shops = useMemo(() => extractData(paginatedShops), [paginatedShops]);

  const onSubmit = useCatalogFormSubmit();

  const [update, { isLoading, error: apiErrors }] = catalogApi.useUpdateOneCatalogsMutation();

  const formMethods = useForm<CatalogFormValues>({
    resolver: classValidatorResolver(CatalogDTO),
    defaultValues: transformToUpdatableFormCatalog(catalog, shops),
  });

  useEffect(() => {
    formMethods.reset(transformToUpdatableFormCatalog(catalog, shops));
  }, [catalog, shops]);

  const onUpdate = async (formData: FormData) => {
    try {
      const updatedCatalog = await update({ _id: catalog?._id!, formData }).unwrap();

      triptych.close({ mode: 'UPDATE', data: updatedCatalog });

      toast.success('Modification effectuée avec succès.')
    } catch (err) {
      const errorMessage = extractApiGenericError(err) ?? 'Une erreur est survenue lors de la modification.';
      toast.error(errorMessage);
    }
  }

  return (
    <Fragment>

      <CatalogCard
        catalog={catalog}
        toggle={toggle}
        formMethods={formMethods}
        onOpenImagePicker={() => cardImageRef.current?.open()} />

      <Panel.Content>

        <Form isLoading={isFetching} formMethods={formMethods} apiErrors={apiErrors}>

          <Panel.FormContainer>

            <Panel.Header>
              <CheckPermissions permissions={['dashboard::catalogs.create']}>
                <Button
                  title="Dupliquer"
                  disabled={!catalog || isFetching}
                  bordered
                  onPress={() => triptych.open({ mode: 'CREATE', data: valeursDeCopieDuCatalogue(catalog as ICatalog) })} />
              </CheckPermissions>

              <CheckPermissions permissions={['dashboard::catalogs.delete']}>
                <Button
                  title="Supprimer"
                  onPress={() => onDelete(catalog!)}
                  bordered
                  important />
              </CheckPermissions>
            </Panel.Header>

            <CatalogForm ref={cardImageRef} />

          </Panel.FormContainer>

          <Panel.Actions>
            <Button
              title="Retour"
              grow
              large
              bordered
              onPress={() => triptych.close()} />

            <Button
              loading={isLoading}
              onPress={() => onSubmit(formMethods, onUpdate)}
              title="Valider"
              grow
              large />
          </Panel.Actions>

        </Form>

      </Panel.Content>
    </Fragment>
  );
};
